import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Loader2,
  Save,
  Trash2,
  Pencil,
  Shield,
  CheckCircle2,
  X,
  Users as UsersIcon,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SCREEN_LABELS = {
  dashboard: "Dashboard",
  jobs: "Vagas",
  candidates: "Candidatos",
  emails: "E-mails",
  whatsapp: "WhatsApp",
  settings: "Configurações",
  access: "Acessos",
};

const ACTION_LABELS = {
  view: "Visualizar",
  create: "Criar",
  edit: "Editar",
  delete: "Excluir",
  upload: "Enviar currículos",
  reanalyze: "Reanalisar",
  send_email: "Enviar e-mail",
  send_whatsapp: "Enviar WhatsApp",
  send: "Enviar",
  company: "Dados da empresa",
  smtp: "SMTP",
  whatsapp: "WhatsApp",
  profiles: "Gerenciar perfis",
  users: "Gerenciar usuários",
};

const emptyForm = () => ({
  id: null,
  name: "",
  description: "",
  data_scope: "own",
  permissions: {},
});

export default function AccessProfilesPanel() {
  const [profiles, setProfiles] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // form state
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        axios.get(`${API}/access/profiles`),
        axios.get(`${API}/access/permissions/catalog`),
      ]);
      setProfiles(pRes.data || []);
      setCatalog(cRes.data || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar perfis");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleNew = () => setEditing(emptyForm());

  const handleEdit = (profile) => {
    setEditing({
      id: profile.id,
      name: profile.name,
      description: profile.description || "",
      data_scope: profile.data_scope || "own",
      permissions: profile.permissions || {},
      is_admin: profile.is_admin,
      is_system: profile.is_system,
    });
  };

  const handleDelete = async (profile) => {
    if (!window.confirm(`Excluir o perfil "${profile.name}"?`)) return;
    try {
      await axios.delete(`${API}/access/profiles/${profile.id}`);
      toast.success("Perfil excluído");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível excluir");
    }
  };

  const togglePermission = (screen, action) => {
    setEditing((prev) => {
      const perms = { ...(prev.permissions || {}) };
      const current = new Set(perms[screen] || []);
      if (current.has(action)) current.delete(action);
      else current.add(action);
      if (current.size === 0) delete perms[screen];
      else perms[screen] = Array.from(current);
      return { ...prev, permissions: perms };
    });
  };

  const handleSave = async () => {
    if (!editing.name.trim()) {
      toast.error("Informe o nome do perfil");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        description: editing.description.trim(),
        data_scope: editing.data_scope,
        permissions: editing.permissions,
      };
      if (editing.id) {
        await axios.put(`${API}/access/profiles/${editing.id}`, payload);
        toast.success("Perfil atualizado");
      } else {
        await axios.post(`${API}/access/profiles`, payload);
        toast.success("Perfil criado");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const screens = useMemo(() => Object.keys(catalog), [catalog]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Carregando perfis…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {!editing && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500 max-w-xl">
            Perfis definem o que cada grupo de usuários pode <strong>fazer</strong> e{" "}
            <strong>ver</strong>. Crie quantos perfis precisar — depois associe-os aos usuários.
          </p>
          <Button
            data-testid="new-profile-button"
            onClick={handleNew}
            className="bg-brand-blue hover:bg-brand-blue-dark text-white"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Novo perfil
          </Button>
        </div>
      )}

      {!editing && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {profiles.map((p) => (
            <div
              key={p.id}
              data-testid={`profile-card-${p.id}`}
              className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col gap-2"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5">
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      p.is_admin
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-blue-50 text-brand-blue"
                    }`}
                  >
                    <Shield className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900 text-sm flex items-center gap-2">
                      {p.name}
                      {p.is_system && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                          padrão
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{p.description || "—"}</div>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEdit(p)}
                    data-testid={`edit-profile-${p.id}`}
                    aria-label="Editar"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  {!p.is_admin && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(p)}
                      data-testid={`delete-profile-${p.id}`}
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      aria-label="Excluir"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <UsersIcon className="w-3 h-3" /> {p.users_count} usuário(s)
                </span>
                <span className="inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  {p.data_scope === "company" ? "Vê dados da empresa" : "Vê apenas seus próprios dados"}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="heading text-base font-semibold text-slate-900">
              {editing.id ? "Editar perfil" : "Novo perfil"}
            </h3>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditing(null)}
              data-testid="cancel-profile-edit"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input
                data-testid="profile-name-input"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="Ex: Gestor de RH"
                disabled={editing.is_admin}
              />
            </div>
            <div className="space-y-2">
              <Label>Escopo dos dados</Label>
              <Select
                value={editing.data_scope}
                onValueChange={(v) => setEditing({ ...editing, data_scope: v })}
                disabled={editing.is_admin}
              >
                <SelectTrigger data-testid="profile-scope-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">Vê todos os dados da empresa</SelectItem>
                  <SelectItem value="own">Vê apenas os próprios dados</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Descrição</Label>
            <Textarea
              data-testid="profile-description-input"
              rows={2}
              value={editing.description}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              placeholder="Para que serve este perfil?"
            />
          </div>

          <div>
            <Label className="mb-2 block">Permissões</Label>
            {editing.is_admin && (
              <div className="text-xs text-slate-500 mb-2">
                O perfil <strong>Administrador</strong> tem acesso total e não pode ser editado.
              </div>
            )}
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-slate-600 text-xs uppercase tracking-wide">
                      Tela
                    </th>
                    <th className="text-left px-4 py-2 font-medium text-slate-600 text-xs uppercase tracking-wide">
                      Ações permitidas
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {screens.map((screen) => {
                    const actions = catalog[screen] || [];
                    const granted = new Set(editing.permissions?.[screen] || []);
                    return (
                      <tr key={screen} className="border-b border-slate-100">
                        <td className="px-4 py-3 align-top font-medium text-slate-700 w-44">
                          {SCREEN_LABELS[screen] || screen}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-2">
                            {actions.map((action) => {
                              const isOn = editing.is_admin ? true : granted.has(action);
                              return (
                                <button
                                  type="button"
                                  key={action}
                                  data-testid={`perm-${screen}-${action}`}
                                  disabled={editing.is_admin}
                                  onClick={() => togglePermission(screen, action)}
                                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                                    isOn
                                      ? "bg-brand-blue border-brand-blue text-white"
                                      : "bg-white border-slate-300 text-slate-600 hover:border-brand-blue hover:text-brand-blue"
                                  } ${editing.is_admin ? "opacity-70 cursor-not-allowed" : ""}`}
                                >
                                  {ACTION_LABELS[action] || action}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              data-testid="save-profile-button"
              onClick={handleSave}
              disabled={saving || editing.is_admin}
              className="bg-brand-blue hover:bg-brand-blue-dark text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
